// "My Results" ecosystem plan, Phase 2 — clinician counterpart of
// MyResults.js. Reads GET /my-patients-results (backend, this same app).
//
// 2026-10-22 — split cleanly from the new MyPatientsSummaries.js (its own
// "My Patients' Summaries" page): this page keeps only the CDS-exclusive
// content (clinical_flags/citations/followup_test_suggestions) and the
// structured test-value tables; ai_summary moved to the new page, per
// explicit user instruction mirroring the patient side's own MyResults.js/
// MySummaries.js split.
//
// Deliberately NOT interactive the way MyResults.js is (no gauge-click
// modal, no per-value/whole-report AI chat) — Phase 2's own plan-doc
// scope is a READ page: clinical flags, citations, and full structured
// values for a clinician's own patients. AI chat for the clinician side
// is tracked as separate, later work, same as the plan's own staged
// sequencing for Phase 1. A plain, read-only presentational component
// set stays here rather than reusing MyResults.js's interactive
// GaugeRow/ValueDetailModal/ToxSection/etc. — those call onOpenValue,
// which opens the per-value AI chat modal backed by a patient-only
// endpoint (/my-results/ask-about-value, 404s for a clinician token);
// reusing them here would mean either a dead click target or threading
// a clinician-specific chat endpoint through components built for a
// different role, neither of which this piece's scope calls for.
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Chip, Alert, TextField, MenuItem, Select, InputAdornment } from "@mui/material";
import { ArrowBack, PersonOutline, WarningAmber, Science, Assignment, Biotech, HourglassEmpty, Search } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import NavUserMenu from "./NavUserMenu";
import { formatEST } from "../dateUtils";
import { displayNameForTestId } from "../testDisplayNames";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
// 2026-10-22 — merged per explicit user instruction: this used to be its
// own REACT_APP_MY_RESULTS_CLINICIAN_ENABLED flag; now shares the SAME
// flag as the patient-facing MyResults.js sibling, since this clinician
// page is the same "patient summary" content, just for the clinician's
// own roster — turns on/off together with MyResults.js, not staged
// separately.
const MY_PATIENTS_RESULTS_ENABLED = process.env.REACT_APP_MY_RESULTS_ENABLED === "true";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

// Same lab-value status vocabulary/coloring as MyResults.js's own
// statusKeyFor/statusColor (kept as a small standalone copy here rather
// than an import — this is a separate page for a separate role, and the
// two vocabularies are small enough that duplicating is cheaper than
// coupling two role-specific pages together for one shared function).
function statusKeyFor(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("critical")) return "critical";
  if (s.includes("high")) return "high";
  if (s.includes("low")) return "low";
  if (s.includes("abnormal") || s.includes("detected")) return "high";
  return "normal";
}
function statusColor(key, isDark) {
  if (key === "normal") return isDark ? "#4ade80" : "#1a7a1a";
  if (key === "critical") return "#991b1b";
  return "#cc0000";
}

function SectionHeader({ icon, label, isDark }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
      {icon}
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: isDark ? "#94a3b8" : "#64748b" }}>{label}</Typography>
    </Box>
  );
}

// clinical_flags/citations have no single fixed shape (see
// _write_report_summary_doc's own comment: both are free-form dicts/
// lists of CDS content) — render defensively: an array of strings/
// objects, or a plain object's own key/value pairs, never a raw
// JSON.stringify dump unless genuinely nothing else is renderable.
// ui-ux-checker finding (2026-10-21) — this used to only test raw
// truthiness; `[]`/`{}` are both truthy in JS, so an empty-but-present
// clinical_flags/citations value rendered a hollow "Clinical flags" box
// with the warning icon and nothing inside it, reading as "something
// failed to load" rather than "no flags."
function hasFreeformContent(data) {
  if (data == null) return false;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") return Object.keys(data).length > 0;
  return true;
}

function FreeformBlock({ data, isDark }) {
  if (data == null) return null;
  if (Array.isArray(data)) {
    if (!data.length) return null;
    return (
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {data.map((item, i) => (
          <Box component="li" key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.5, lineHeight: 1.55 }}>
            {typeof item === "object" && item !== null
              ? Object.entries(item).map(([k, v], j) => (
                  // ui-ux-checker finding — this branch used `String(v)`
                  // unconditionally, so a nested object value (plausible
                  // for CDS content) rendered the literal text
                  // "[object Object]" instead of the plain-object
                  // branch's own correct JSON.stringify guard below.
                  <span key={j}>{j > 0 ? " — " : ""}<b>{k}:</b> {typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}</span>
                ))
              : String(item)}
          </Box>
        ))}
      </Box>
    );
  }
  if (typeof data === "object") {
    const entries = Object.entries(data);
    if (!entries.length) return null;
    return (
      <Box>
        {entries.map(([k, v], i) => (
          <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.4 }}>
            <b>{k}:</b> {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </Typography>
        ))}
      </Box>
    );
  }
  return <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155" }}>{String(data)}</Typography>;
}

// Read-only lab-value row — the non-interactive counterpart of
// MyResults.js's clickable GaugeRow.
function StaticValueRow({ row, isDark }) {
  const key = statusKeyFor(row.status);
  const color = statusColor(key, isDark);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{row.test}</Typography>
        <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
          {row.value} {row.unit || ""} {row.reference_range ? `· Ref: ${row.reference_range}` : ""}
        </Typography>
      </Box>
      <Chip size="small" label={row.status || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
    </Box>
  );
}

// ui-ux-checker finding — mirrors MyResults.js's own ToxSection
// severity classification (Illicit->critical, Inconsistent->high,
// Not Tested->neutral, Consistent->normal) so a positive illicit
// finding and a clean negative result read as visually distinct here
// too, not identical plain rows.
function toxSeverityKey(section) {
  const s = String(section || "").toUpperCase();
  if (s.includes("ILLICIT")) return "critical";
  if (s.includes("INCONSISTENT")) return "high";
  if (s.includes("NOT TESTED")) return "normal"; // neutral — statusColor has no gray bucket here, closest non-alarm color
  return "normal";
}

function StructuredResultsSection({ structured, isDark }) {
  if (!structured) return null;
  if (structured.kind === "standard") {
    const rows = structured.full_results || [];
    if (!rows.length) return null;
    return <Box>{rows.map((row, i) => <StaticValueRow key={`${row.test}-${i}`} row={row} isDark={isDark} />)}</Box>;
  }
  // tox/drug_screen — same categorized rows MyResults.js shows the
  // patient, read-only here (no click target).
  const rows = structured.kind === "tox"
    ? [...(structured.first_page_summary || []), ...(structured.second_section || [])]
    : (structured.consistent || []);
  const realRows = rows.filter((r) => {
    const label = r?.["Drug Prescribed"] ?? r?.["Analyte Detected"] ?? r?.Drug;
    return label != null && String(label).trim() !== "" && label !== "N/A" && label !== "__EMPTY__";
  });
  const validity = structured.kind === "drug_screen" && Array.isArray(structured.validity) ? structured.validity : [];
  if (!realRows.length && !validity.length) return null;
  return (
    <Box>
      {realRows.map((r, i) => {
        const label = r["Drug Prescribed"] ?? r["Analyte Detected"] ?? r.Drug;
        const key = structured.kind === "tox" ? toxSeverityKey(r.Section)
          : (String(r.Section || "").toUpperCase().includes("INCONSISTENT") ? "high" : "normal");
        const color = statusColor(key, isDark);
        return (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b" }}>{label}</Typography>
              {r.Section && <Typography sx={{ fontSize: 11.5, color: isDark ? "#94a3b8" : "#64748b" }}>{r.Section}</Typography>}
            </Box>
            <Chip size="small" label={r["Result (ng/mL)"] || r.Result || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
          </Box>
        );
      })}
      {validity.length > 0 && (
        <Box sx={{ mt: realRows.length ? 1.5 : 0 }}>
          <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Specimen validity" isDark={isDark} />
          {validity.map((v, i) => {
            const key = v.Flagged ? "high" : "normal";
            const color = statusColor(key, isDark);
            return (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, px: 1.5 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#e5e7eb" : "#1e293b", flex: 1 }}>
                  {v["Validity Test"]}
                </Typography>
                <Chip size="small" label={v["Test Outcome"] || "—"} sx={{ fontSize: 11, fontWeight: 700, bgcolor: `${color}1a`, color }} />
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// Proove-typed stats_json — the same three shapes MyResults.js's own
// PgxSection/ScoreStrip read, rendered as a plain read-only list here.
// ui-ux-checker finding — this used to render only genes/medications/
// dosing-guidance for PGx and only the score fields + COMT for opioid_
// risk/pain_perception, dropping stats.genotypes/foreign_scores/
// history_questions entirely even though the sibling MyResults.js
// renders all of them (GenotypesSection/ForeignScoresSection/
// HistoryQuestionsSection) — real content missing, not a UI-chrome
// scope trim.
function GenotypesList({ genotypes, isDark }) {
  if (!Array.isArray(genotypes) || !genotypes.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genotypes.length})`} isDark={isDark} />
      {genotypes.map((g, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{g?.gene}</b>: {g?.result || "—"}{g?.rs_number ? ` (${g.rs_number})` : ""}
        </Typography>
      ))}
    </Box>
  );
}

function ForeignScoresList({ scores, isDark }) {
  if (!Array.isArray(scores) || !scores.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Additional scores" isDark={isDark} />
      {scores.map((s, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{s?.label}:</b> {s?.value ?? "—"}
        </Typography>
      ))}
    </Box>
  );
}

function HistoryQuestionsList({ items, isDark }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <SectionHeader icon={<Assignment sx={{ fontSize: 16, color: BRAND.gold }} />} label="History questionnaire" isDark={isDark} />
      {items.map((q, i) => (
        <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{q?.question}:</b> {q?.answer ?? "—"}
        </Typography>
      ))}
    </Box>
  );
}

function ProoveStatsSection({ stats, rtid, isDark }) {
  if (!stats) return null;
  if (rtid === "proove_pgx") {
    const genes = Array.isArray(stats.genes) ? stats.genes : [];
    const riskMgmt = Array.isArray(stats.risk_management) ? stats.risk_management : [];
    const hasAnything = genes.length || riskMgmt.length || stats.current_patient_medications || stats.dosing_guidance_summary;
    if (!hasAnything) {
      return (
        <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 1, textAlign: "center" }}>
          No drug-metabolism findings were extracted from this report.
        </Typography>
      );
    }
    return (
      <Box>
        {genes.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <SectionHeader icon={<Biotech sx={{ fontSize: 16, color: BRAND.gold }} />} label={`Gene findings (${genes.length})`} isDark={isDark} />
            {genes.map((g, i) => (
              <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
                <b>{g.gene}</b>: {g.genotype || "—"}{g.phenotype ? ` (${g.phenotype})` : ""}
              </Typography>
            ))}
          </Box>
        )}
        {stats.current_patient_medications && (
          <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 1 }}>
            <b>Flagged medications:</b> {stats.current_patient_medications}
          </Typography>
        )}
        {riskMgmt.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <SectionHeader icon={<WarningAmber sx={{ fontSize: 16, color: BRAND.gold }} />} label="Risk management findings" isDark={isDark} />
            {riskMgmt.map((r, i) => (
              <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
                <b>{r.condition}:</b> {r.finding}
              </Typography>
            ))}
          </Box>
        )}
        {stats.dosing_guidance_summary && (
          <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155" }}>
            <b>Dosing guidance:</b> {stats.dosing_guidance_summary}
          </Typography>
        )}
      </Box>
    );
  }
  const scoreFields = rtid === "proove_opioid_risk"
    ? [["proove_ori", "Opioid Risk Index"], ["ort", "ORT"], ["phq2", "PHQ-2"]]
    : [["self_reported_pain", "Self-Reported Pain"], ["pss", "PSS"]];
  const present = scoreFields.filter(([f]) => stats[f] && (stats[f].score != null || stats[f].level));
  const hasComt = rtid === "proove_pain_perception" && !!stats.comt_predisposition?.level;
  const hasAnything = present.length || hasComt || stats.genotypes?.length || stats.foreign_scores?.length
    || (rtid === "proove_opioid_risk" && stats.history_questions?.length);
  if (!hasAnything) {
    return (
      <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#94a3b8", py: 1, textAlign: "center" }}>
        No findings were extracted from this report.
      </Typography>
    );
  }
  return (
    <Box>
      {present.map(([f, label]) => (
        <Typography key={f} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 0.3 }}>
          <b>{label}:</b> {stats[f].score ?? ""}{stats[f].level ? ` (${stats[f].level})` : ""}
        </Typography>
      ))}
      {hasComt && (
        <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", mb: 1.5 }}>
          <b>Genetic Pain Sensitivity (COMT):</b> {stats.comt_predisposition.level}
        </Typography>
      )}
      <GenotypesList genotypes={stats.genotypes} isDark={isDark} />
      <ForeignScoresList scores={stats.foreign_scores} isDark={isDark} />
      {rtid === "proove_opioid_risk" && <HistoryQuestionsList items={stats.history_questions} isDark={isDark} />}
    </Box>
  );
}

const _PROOVE_TYPED_IDS = new Set(["proove_pgx", "proove_opioid_risk", "proove_pain_perception"]);

function PatientResultCard({ result, isDark }) {
  const structured = result.structured_results;
  const stats = result.stats_json;
  const rtid = result.report_type_id;
  const isProoveTyped = _PROOVE_TYPED_IDS.has(rtid);
  const patientName = [result.patient_first_name, result.patient_last_name].filter(Boolean).join(" ") || "Unknown patient";

  return (
    <Box sx={{
      borderRadius: 3, overflow: "hidden", mb: 3,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
    }}>
      <Box sx={{
        px: { xs: 2, sm: 3 }, py: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`,
      }}>
        <PersonOutline sx={{ color: BRAND.gold, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, color: "#fff", fontSize: 14.5 }}>{patientName}</Typography>
        <Chip label={String(rtid || "").replace(/_/g, " ")} size="small"
          sx={{ textTransform: "capitalize", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem",
            maxWidth: { xs: "55%", sm: "40%" } }} />
        {stats?.is_proove_branded && (
          <Chip label="Proove Genomics" size="small" sx={{ bgcolor: "rgba(201,169,110,0.28)", color: "#fff", fontSize: "0.62rem", fontWeight: 700 }} />
        )}
        {result.generated_at && (
          <Chip label={formatEST(result.generated_at, { dateOnly: true })} size="small"
            sx={{ ml: "auto", bgcolor: "rgba(255,255,255,0.14)", color: "#fff", fontSize: "0.68rem" }} />
        )}
      </Box>

      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        {result.patient_dob && (
          <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mb: 2 }}>
            DOB: {result.patient_dob}
          </Typography>
        )}

        {/* 2026-10-22 — ai_summary moved to the new MyPatientsSummaries.js
            (its own "My Patients' Summaries" page), per explicit user
            instruction splitting this page cleanly back into "test
            results" vs "summaries" content, mirroring the patient side's
            own MyResults.js/MySummaries.js split. This page keeps only
            the CDS-exclusive content (clinical_flags/citations/
            followup_test_suggestions) and the structured value tables. */}

        {hasFreeformContent(result.clinical_flags) && (
          <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, bgcolor: isDark ? "rgba(153,27,27,0.12)" : "rgba(153,27,27,0.06)" }}>
            <SectionHeader icon={<WarningAmber sx={{ fontSize: 16, color: "#991b1b" }} />} label="Clinical flags" isDark={isDark} />
            <FreeformBlock data={result.clinical_flags} isDark={isDark} />
          </Box>
        )}

        {isProoveTyped && result.stats_extraction_status === "pending" ? (
          // 2026-10-21 fix (downstream-mismatch-checker finding) — the
          // sibling patient-facing MyResults.js already has this exact
          // affordance for this exact backend condition; this page had
          // none, so a Proove-typed report whose stats extraction hasn't
          // finished yet rendered as a card with no score content and
          // no explanation at all.
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, py: 2, color: isDark ? "#94a3b8" : "#64748b" }}>
            <HourglassEmpty sx={{ fontSize: 20, opacity: 0.7 }} />
            <Typography sx={{ fontStyle: "italic", fontSize: "0.9rem" }}>
              This report's structured data is still being generated — check back shortly.
            </Typography>
          </Box>
        ) : isProoveTyped ? (
          <ProoveStatsSection stats={stats} rtid={rtid} isDark={isDark} />
        ) : (
          <StructuredResultsSection structured={structured} isDark={isDark} />
        )}

        {hasFreeformContent(result.citations) && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
            <SectionHeader icon={<Science sx={{ fontSize: 16, color: BRAND.gold }} />} label="Citations" isDark={isDark} />
            <FreeformBlock data={result.citations} isDark={isDark} />
          </Box>
        )}

        {Array.isArray(result.followup_test_suggestions) && result.followup_test_suggestions.length > 0 && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
            <SectionHeader icon={<Assignment sx={{ fontSize: 16, color: BRAND.gold }} />} label="Follow-up test suggestions" isDark={isDark} />
            {result.followup_test_suggestions.map((s, i) => (
              <Typography key={i} sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#334155", ml: 0.3, mb: 0.3 }}>
                • <b>{displayNameForTestId(s.test_id)}</b>{s.rationale ? ` — ${s.rationale}` : ""}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function MyPatientsResults() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [results, setResults] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null); // { message, severity } | null

  // 2026-10-26 addition (user-directed — "clinicians should be able to
  // search for patients, report types, dates, date of births") — this
  // page already fetches the clinician's full (capped-at-100) result
  // set in one call, so filtering is done client-side against the
  // already-loaded `results` array, same pattern as the admin Sales
  // Reps roster filter built earlier this session — no new backend
  // endpoint needed for a roster this size.
  const [searchTerm, setSearchTerm] = useState("");
  const [reportTypeFilter, setReportTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const reportTypeOptions = useMemo(
    () => Array.from(new Set(results.map((r) => r.report_type_id).filter(Boolean))).sort(),
    [results],
  );

  const filteredResults = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return results.filter((r) => {
      if (reportTypeFilter && r.report_type_id !== reportTypeFilter) return false;
      // generated_at is a real ISO 8601 timestamp (see formatEST's own
      // call on it below) — plain string comparison against a
      // YYYY-MM-DD boundary sorts correctly without needing a Date
      // parse, same as every other date-range filter already in this
      // codebase.
      if (dateFrom && r.generated_at && r.generated_at < dateFrom) return false;
      if (dateTo && r.generated_at && r.generated_at > `${dateTo}T23:59:59`) return false;
      if (!term) return true;
      const name = [r.patient_first_name, r.patient_last_name].filter(Boolean).join(" ").toLowerCase();
      const rtidHuman = displayNameForTestId(r.report_type_id).toLowerCase();
      const dob = String(r.patient_dob || "").toLowerCase();
      return name.includes(term) || rtidHuman.includes(term) || dob.includes(term);
    });
  }, [results, searchTerm, reportTypeFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (!MY_PATIENTS_RESULTS_ENABLED) {
      setErrorState({ message: "My Patients' Results isn't available yet.", severity: "info" });
      setLoading(false);
      return;
    }
    axios.get(`${API_BASE}/my-patients-results`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (res.data?.feature_disabled) {
          setErrorState({ message: "My Patients' Results isn't available yet.", severity: "info" });
          return;
        }
        setResults(res.data.results || []);
        // ui-ux-checker finding — the backend caps at 100 and returns
        // total_count/truncated; this used to be silently ignored, so a
        // clinician with more than 100 completed results would see only
        // the 100 most recent with no indication the list was incomplete.
        setTruncated(!!res.data.truncated);
      })
      .catch(() => setErrorState({ message: "Unable to load your patients' results right now.", severity: "error" }))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <Box sx={{
        px: 3, py: 2, bgcolor: isDark ? "#111827" : "#fff",
        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
        display: "flex", alignItems: "center", gap: 2,
      }}>
        <Box onClick={() => navigate("/home")} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate("/home"); } }}
          sx={{
            display: "flex", alignItems: "center", gap: 0.5,
            color: isDark ? "#94a3b8" : "#64748b", fontSize: 14.5, cursor: "pointer",
            borderRadius: "8px", px: 1.2, py: 0.6, transition: "all 0.2s",
            "&:hover": { color: isDark ? BRAND.gold : BRAND.navy, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.06)" },
            "&:focus-visible": { outline: `2px solid ${BRAND.gold}`, outlineOffset: 2 },
          }}
        >
          <ArrowBack sx={{ fontSize: 15 }} />
          <span>Home</span>
        </Box>
        <Box sx={{ width: "1px", height: 20, bgcolor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.1)" }} />
        <Typography sx={{ fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, fontSize: 18, flex: 1 }}>My Patients' Results</Typography>
        <ThemeToggle />
        <NavUserMenu />
      </Box>

      <Box sx={{ maxWidth: 820, mx: "auto", px: 3, py: 5 }}>
        <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", maxWidth: 520, mb: 4 }}>
          Completed test results for your patients — clinical flags, citations, and full structured values.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
        ) : errorState ? (
          <Alert severity={errorState.severity} sx={{ borderRadius: 2 }}>{errorState.message}</Alert>
        ) : results.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
            <Science sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography>No completed results yet for your ordered tests.</Typography>
          </Box>
        ) : (
          <>
            {truncated && (
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mb: 2, fontStyle: "italic" }}>
                Showing the 100 most recent results.
              </Typography>
            )}
            <Box sx={{
              display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3, p: 2, borderRadius: 2,
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.03)",
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)",
            }}>
              <TextField size="small" placeholder="Search patient, DOB, or report type…"
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ flex: "1 1 220px", minWidth: 200 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: isDark ? "#64748b" : "#94a3b8" }} /></InputAdornment> }} />
              <Select size="small" displayEmpty value={reportTypeFilter}
                onChange={(e) => setReportTypeFilter(e.target.value)} sx={{ minWidth: 170 }}>
                <MenuItem value="">All report types</MenuItem>
                {reportTypeOptions.map((rtid) => (
                  <MenuItem key={rtid} value={rtid}>{displayNameForTestId(rtid)}</MenuItem>
                ))}
              </Select>
              <TextField size="small" label="From" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
              <TextField size="small" label="To" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }} />
            </Box>
            {filteredResults.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
                <Search sx={{ fontSize: 32, mb: 1, opacity: 0.4 }} />
                <Typography>No results match these filters.</Typography>
              </Box>
            ) : (
              filteredResults.map((r) => <PatientResultCard key={r._id} result={r} isDark={isDark} />)
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
